import { Component, type ErrorInfo, type ReactNode } from "react"
import { AlertTriangle } from "lucide-react"

import { Button } from "@/components/ui/button"

/**
 * Top-level ErrorBoundary (SPEC FE-1 / R3b).
 *
 * Wraps the route tree so a render error in any page shows a styled metabus
 * fallback (heading + message + reload) instead of white-screening the whole app
 * - GHL degrades gracefully; previously we had NO ErrorBoundary anywhere. Wires
 * NO new data and does not change routing access.
 */
interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional custom fallback; defaults to the metabus fallback below. */
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the error for diagnostics without crashing the app.
    console.error("ErrorBoundary caught a render error:", error, info)
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div
          className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center"
          role="alert"
          data-testid="error-boundary-fallback"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgba(239,68,68,0.12)] text-destructive">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground">
            Something went wrong
          </h1>
          <p className="mt-2 max-w-md text-sm text-muted-foreground">
            An unexpected error interrupted this page. Reloading usually fixes it.
            If it keeps happening, let us know.
          </p>
          <Button className="mt-6" onClick={this.handleReload}>
            Reload page
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}
